import { useState } from "react";
import { toast } from "sonner";
import { UserCog, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { PhoneInput } from "@/components/PhoneInput";
import { BILLING_COUNTRIES, billingConfig, validateBilling } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Account() {
  const { user, updateProfile } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "+39",
    person_type: user?.person_type || "individual",
    company_name: user?.company_name || "",
    billing_country: user?.billing_country || "IT",
    vat_number: user?.vat_number || "",
    tax_code: user?.tax_code || "",
    sdi_code: user?.sdi_code || "",
    pec_email: user?.pec_email || "",
    billing_address: user?.billing_address || "",
    address: user?.address || "",
  });
  const [saving, setSaving] = useState(false);
  const isCompany = form.person_type === "company";
  const bcfg = billingConfig(form.billing_country);

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const isGoogle = user?.auth_provider === "google";

  const save = async () => {
    if (isCompany) {
      const errs = validateBilling(form.billing_country, form);
      if (errs.length) { toast.error(errs[0]); return; }
    }
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success(t("account.saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if ((pw.new_password || "").length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Le password non coincidono"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password || undefined, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="account-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><UserCog size={26} /> {t("account.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("account.subtitle")}</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="space-y-1.5"><Label>{t("auth.personType")}</Label>
          <Select value={form.person_type} onValueChange={(v) => setForm({ ...form, person_type: v })}>
            <SelectTrigger data-testid="account-person-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">{t("auth.individual")}</SelectItem>
              <SelectItem value="company">{t("auth.company2")}</SelectItem>
            </SelectContent>
          </Select></div>

        <div className="space-y-1.5"><Label>{isCompany ? t("auth.companyName") : t("auth.name")}</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" /></div>

        <div className="space-y-1.5"><Label>Email</Label>
          <Input value={user?.email || ""} disabled data-testid="account-email" /></div>

        <div className="space-y-1.5"><Label>{t("auth.phone")}</Label>
          <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="account-phone" /></div>

        {isCompany && (
          <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="account-billing">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Dati di fatturazione</p>
            <div className="space-y-1.5"><Label>Paese di fatturazione</Label>
              <Select value={form.billing_country} onValueChange={(v) => setForm({ ...form, billing_country: v })}>
                <SelectTrigger data-testid="account-billing-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BILLING_COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{bcfg.vatLabel}</Label>
                <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder={bcfg.vatHint} data-testid="account-vat" />
                {bcfg.vatHint && <p className="text-[11px] text-slate-400">{bcfg.vatHint}</p>}</div>
              <div className="space-y-1.5"><Label>Codice Fiscale</Label>
                <Input value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} data-testid="account-taxcode" /></div>
            </div>
            {bcfg.italy && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Codice SDI</Label>
                  <Input value={form.sdi_code} onChange={(e) => setForm({ ...form, sdi_code: e.target.value })} placeholder="7 caratteri" maxLength={7} data-testid="account-sdi" /></div>
                <div className="space-y-1.5"><Label>PEC</Label>
                  <Input type="email" value={form.pec_email} onChange={(e) => setForm({ ...form, pec_email: e.target.value })} data-testid="account-pec" /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label>Indirizzo di fatturazione</Label>
              <Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="account-billing-address" /></div>
          </div>
        )}

        <div className="space-y-1.5"><Label>{t("account.address")}</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="account-address" /></div>

        <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="account-save">
          {saving ? "..." : t("account.save")}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="account-password-card">
        <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Lock size={18} /> Password</h2>
        {isGoogle && (
          <p className="text-xs text-slate-500" data-testid="account-google-hint">Accedi con Google. Puoi impostare una password per accedere anche con email — lascia vuoto il campo «Password attuale».</p>
        )}
        <div className="space-y-1.5"><Label>Password attuale</Label>
          <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="account-current-password" autoComplete="current-password" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Nuova password</Label>
            <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="account-new-password" autoComplete="new-password" /></div>
          <div className="space-y-1.5"><Label>Conferma nuova password</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="account-confirm-password" autoComplete="new-password" /></div>
        </div>
        <Button onClick={changePassword} disabled={pwSaving} className="bg-blue-900 hover:bg-blue-800" data-testid="account-change-password-btn">
          {pwSaving ? "..." : "Aggiorna password"}
        </Button>
      </div>
    </div>
  );
}
